import { Box, Flex } from "@mantine/core";
import type { ReactNode } from "react";

export type CompanyProfileHeaderLayoutProps = {
  logo: ReactNode;
  /** Beside logo only (row 1). */
  title: ReactNode;
  /** Row 1 top-right, beside company name (e.g. GTID). */
  gtidRow?: ReactNode;
  /** Below row 1, right-aligned (e.g. verification stepper). */
  verification?: ReactNode;
  /** Row 1 top-right, below or beside gtidRow (bookmark, chips). */
  topActions?: ReactNode;
  /** Full-width rows below logo, flush left. */
  country?: ReactNode;
  phoneAndEmail?: ReactNode;
  website?: ReactNode;
  afterDetails?: ReactNode;
};

/**
 * Skeleton: logo + title on one row; all other lines start at the card’s left edge (below the logo row).
 */
export default function CompanyProfileHeaderLayout({
  logo,
  title,
  gtidRow,
  verification,
  topActions,
  country,
  phoneAndEmail,
  website,
  afterDetails,
}: CompanyProfileHeaderLayoutProps) {
  const topRight =
    gtidRow || topActions ? (
      <Flex direction="column" align="flex-end" gap={4} style={{ flexShrink: 0 }}>
        {gtidRow}
        {topActions}
      </Flex>
    ) : null;

  return (
    <Box w="100%" miw={0}>
      {/* Row 1: logo + company name + GTID / actions (top-right) */}
      <Flex gap="md" align="flex-start" wrap="nowrap" w="100%" miw={0}>
        <Box style={{ flexShrink: 0 }}>{logo}</Box>
        <Flex justify="space-between" align="flex-start" gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Box style={{ flex: 1, minWidth: 0 }}>{title}</Box>
          {topRight}
        </Flex>
      </Flex>

      {verification ? (
        <Flex justify="flex-end" align="flex-start" gap="sm" mt={8} wrap="wrap" w="100%">
          {verification}
        </Flex>
      ) : null}

      {(country || phoneAndEmail || website) && (
        <Flex gap="md" wrap="wrap" align="center" mt={6} w="100%">
          {country}
          {phoneAndEmail}
          {website}
        </Flex>
      )}

      {afterDetails ? <Box w="100%">{afterDetails}</Box> : null}
    </Box>
  );
}
